import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Loader2, CheckCircle } from 'lucide-react';

export default function MarketingSignupPage() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      navigate('/app/select-school', { replace: true });
    }
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  const features = [
    'Quick attendance tracking',
    'Student behaviour logging',
    'AI-powered lesson summaries',
    'Classroom management tools',
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50/80 to-white flex flex-col" data-testid="signup-page">
      <header className="p-4">
        <Link to="/" className="flex items-center gap-2">
          <img src="/logo.png" alt="Reliefify" className="w-10 h-10 object-contain" />
          <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Reliefify</span>
        </Link>
      </header>

      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
            <div className="text-center mb-6">
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Start your free trial
              </h1>
              <p className="text-slate-600">14 days free, then $4.99/month</p>
            </div>

            <div className="bg-teal-50 rounded-xl p-4 mb-6">
              <ul className="space-y-2 text-sm">
                {features.map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-teal-800">
                    <CheckCircle className="w-4 h-4 text-emerald-600 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <Button
              onClick={login}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-medium h-14 text-base shadow-sm hover:shadow-md transition-shadow"
              data-testid="google-signup-btn"
            >
              <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign up with Google
            </Button>

            <p className="text-xs text-slate-400 text-center mt-4">
              No credit card required. Cancel anytime.
            </p>

            <p className="text-center mt-6 text-slate-600 text-sm">
              Already have an account?{' '}
              <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </main>

      <footer className="p-4 text-center">
        <p className="text-xs text-slate-400">
          <Link to="/" className="hover:text-slate-600">&larr; Back to home</Link>
        </p>
      </footer>
    </div>
  );
}
